// Subscriptions of self-install trackers (task 6, phase 3): status chip, manual renewal form,
// and the tracker's box on the unit page with its paid periods.
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { SubStatus, Subscription, SubscriptionPeriod, subscriptions } from '../api';
import { useAuth } from '../auth';
import { dayOnly, daysFrom, SUB_LABEL } from '../format';
import { Panel } from './Panels';

export function SubChip({ status }: { status: SubStatus }) {
  return <span className={`chip sub-${status}`}>{SUB_LABEL[status]}</span>;
}

// "12 Nov 2026 · in 28 days" / "· 3 days ago".
export function UntilText({ sub }: { sub: Subscription }) {
  const d = daysFrom(sub.paidUntil);
  const rel = d === 0 ? 'today' : d > 0 ? `in ${d} day${d === 1 ? '' : 's'}` : `${-d} day${d === -1 ? '' : 's'} ago`;
  return <>{dayOnly(sub.paidUntil)} <span className="muted small">· {rel}</span></>;
}

const addYears = (iso: string, years: number) => { const d = new Date(iso); d.setFullYear(d.getFullYear() + years); return d.toISOString(); };

export function RenewForm({ trackerId, nextStart, paidUntil, onCancel, onDone }: {
  trackerId: number; nextStart: string; paidUntil: string | null; onCancel: () => void; onDone: (msg: string) => void;
}) {
  const [years, setYears] = useState(1);
  const [startToday, setStartToday] = useState(false);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const today = new Date().toISOString();
  const start = startToday ? today : nextStart;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const euros = amount.trim() ? Number(amount.replace(',', '.')) : null;
    if (euros !== null && !(euros >= 0)) { setError('The amount must be a number of euro, e.g. 120 or 99.50.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await subscriptions.renew(trackerId, { years, startToday, amountCents: euros === null ? null : Math.round(euros * 100), notes: notes.trim() || null });
      onDone(`Renewed until ${dayOnly(r.end)}.`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  // Continues straight after the paid period, unless that ended more than the grace days ago.
  const continues = !!paidUntil && nextStart === paidUntil;
  // In grace (ended, not long ago) staff may start from today so the gap isn't charged.
  const inGrace = continues && new Date(paidUntil!) < new Date();
  return (
    <form className="card form renew" onSubmit={submit}>
      <div className="grid2">
        <label>Renew for
          <select value={years} onChange={(e) => setYears(Number(e.target.value))}>
            <option value={1}>1 year</option><option value={2}>2 years</option><option value={3}>3 years</option>
          </select>
        </label>
        <label>Amount paid (€, optional)<input type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 120" /></label>
      </div>
      <label>Note (optional)<input type="text" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. paid by bank transfer, invoice 1234" /></label>
      {inGrace && (
        <label className="check"><input type="checkbox" checked={startToday} onChange={(e) => setStartToday(e.target.checked)} /> Start from today instead of {dayOnly(paidUntil!)} (the days since it ended aren't charged)</label>
      )}
      <p className="small">New period: <strong>{dayOnly(start)} – {dayOnly(addYears(start, years))}</strong>
        <span className="muted">{startToday ? ' (from today)' : continues ? ' (straight after the current one)' : ' (the last period ended a while ago, so it starts today)'}</span></p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        <button className="primary" disabled={busy}>{busy ? 'Renewing…' : 'Renew'}</button>
        <button type="button" className="link" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

const SOURCE_LABEL = { initial_purchase: 'Bought with the tracker', manual_staff: 'Renewed by staff', stripe: 'Paid online' } as const;

// On the unit page of an activated self-install tracker.
export function SubscriptionBox({ trackerId, onSaved }: { trackerId: number; onSaved: (msg: string) => void }) {
  const { user } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof subscriptions.ofTracker>> | null>(null);
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => { subscriptions.ofTracker(trackerId).then(setData).catch((e) => setError(e.message)); }, [trackerId]);
  useEffect(load, [load]);
  const remove = async (p: SubscriptionPeriod) => {
    if (!window.confirm(`Remove the renewal ${dayOnly(p.start)} – ${dayOnly(p.end)}? Only do this if it was entered by mistake.`)) return;
    try { await subscriptions.remove(p.id); load(); onSaved('Renewal removed.'); } catch (e) { setError((e as Error).message); }
  };
  const s = data?.subscription;
  return (
    <Panel title="Subscription" editing={false} actions={data && !renewing && <button className="link" onClick={() => setRenewing(true)}>Renew</button>}>
      {error && <p className="error" role="alert">{error}</p>}
      {data && (
        <>
          {s ? (
            <dl className="facts">
              <dt>Status</dt><dd><SubChip status={s.status} /></dd>
              <dt>Paid until</dt><dd><UntilText sub={s} /></dd>
              {s.status !== 'active' && <><dt>Hidden in app</dt><dd>{new Date(s.hiddenFrom) <= new Date() ? 'yes, since' : 'from'} {dayOnly(s.hiddenFrom)}</dd></>}
            </dl>
          ) : <p className="muted small">No paid period yet.</p>}
          {renewing && (
            <RenewForm trackerId={trackerId} nextStart={data.nextStart} paidUntil={s?.paidUntil ?? null} onCancel={() => setRenewing(false)}
              onDone={(msg) => { setRenewing(false); load(); onSaved(msg); }} />
          )}
          {data.periods.length > 0 && (
            <details>
              <summary className="small">Paid periods ({data.periods.length})</summary>
              <ul className="small periods">
                {data.periods.map((p) => (
                  <li key={p.id}>
                    <strong>{dayOnly(p.start)} – {dayOnly(p.end)}</strong> · {SOURCE_LABEL[p.source]}
                    {p.amountCents !== null && <> · €{(p.amountCents / 100).toFixed(2)}</>}
                    {p.by && !p.by.customer && <> · {p.by.name}</>}
                    {p.notes && <div className="muted">{p.notes}</div>}
                    {p.removable && user?.role === 'admin' && <div><button className="link danger" onClick={() => void remove(p)}>Remove (mistake)</button></div>}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <p className="muted small">After the paid-until date and {data.graceDays} days' grace, the vehicle disappears from the customer's app. Positions keep being recorded, so renewing shows everything again.</p>
        </>
      )}
    </Panel>
  );
}
